import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section 6.3: API Configuration
// withCredentials=true enables sending session cookies with every request
// No Authorization header needed - backend resolves user from session
const http = axios.create({
  baseURL: API,
  withCredentials: true, // Send cookies (including __Host-session) with every request
});

function getCookie(name) {
  const prefix = `${name}=`;
  return document.cookie.split(";").map((value) => value.trim())
    .find((value) => value.startsWith(prefix))?.slice(prefix.length);
}

http.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options", "trace"].includes(method)) {
    // Section 5.7: CSRF protection for mutations
    const csrfToken = getCookie("csrf_token");
    if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
  }
  return config;
});

// El backend responde 401 cuando la sesión expiró o ya no existe. Avisamos a la app
// (AuthProvider) para que limpie el usuario y mande a login en vez de dejar al
// usuario viendo errores sueltos. Los endpoints de login no cuentan: ahí un 401
// significa credenciales inválidas.
export const SESSION_EXPIRED_EVENT = "lala:session-expired";

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    if (error.response?.status === 401 && !url.startsWith("/auth/login") && !url.startsWith("/auth/entra")) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  // Section 6.6: Only show non-sensitive error messages
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Section 6.2: Session recovery on app load
// Section 6.5: Logout endpoint
export const auth = {
  login: (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  // Section 6.2: Fetch current user from session
  // Called on app initialization to restore session state
  me: () => http.get("/auth/me").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    http.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
};

export const provisioning = {
  plan: () => http.post("/admin/users/provision/plan").then((r) => r.data),
  apply: () => http.post("/admin/users/provision/apply").then((r) => r.data),
  downloadCredentials: (regenerate = false) =>
    http.post(`/admin/users/provision/credentials?regenerate=${regenerate}`, null, { responseType: "blob" }),
};

export const suppliers = {
  list: (params = {}) => http.get("/suppliers", { params }).then((r) => r.data),
  get: (id) => http.get(`/suppliers/${id}`).then((r) => r.data),
  create: (body) => http.post("/suppliers", body).then((r) => r.data),
  update: (id, body) => http.put(`/suppliers/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/suppliers/${id}`).then((r) => r.data),
  history: (id) => http.get(`/suppliers/${id}/history`).then((r) => r.data),
  badge: (id) => http.get(`/suppliers/${id}/badge`).then((r) => r.data),
  setHidden: (id, hidden) => http.put(`/admin/suppliers/${id}/hidden`, { hidden }).then((r) => r.data),
};

export const roi = {
  summary: () => http.get("/roi/summary").then((r) => r.data),
  list: (params = {}) => http.get("/roi/projects", { params }).then((r) => r.data),
  create: (body) => http.post("/roi/projects", body).then((r) => r.data),
  validate: (id) => http.post(`/roi/projects/${id}/validate`).then((r) => r.data),
  remove: (id) => http.delete(`/roi/projects/${id}`).then((r) => r.data),
};

export const evaluations = {
  list: (params = {}) => http.get("/evaluations", { params }).then((r) => r.data),
  create: (body) => http.post("/evaluations", body).then((r) => r.data),
  importCSV: (file) => {
    const form = new FormData();
    form.append("file", file);
    return http
      .post("/evaluations/import-csv", form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
};

export const dashboard = {
  stats: (params = {}) => http.get("/dashboard/stats", { params }).then((r) => r.data),
  pillar: (pillarKey, params = {}) => http.get(`/dashboard/pillar/${pillarKey}`, { params }).then((r) => r.data),
};

export const program = {
  publicInfo: () => http.get("/public/program").then((r) => r.data),
  periodicities: () => http.get("/program/periodicities").then((r) => r.data),
  setPeriodicities: (periodicities) => http.put("/admin/pillar-periodicities", { periodicities }).then((r) => r.data),
  supplierReadonly: () => http.get("/program/supplier-readonly").then((r) => r.data),
  setSupplierReadonly: (enabled) => http.put("/admin/supplier-readonly", { enabled }).then((r) => r.data),
  scoreBadge: () => http.get("/program/score-badge-config").then((r) => r.data),
  setScoreBadge: (body) => http.put("/admin/program/score-badge-config", body).then((r) => r.data),
};

export const kpiCatalog = {
  list: (params = {}) => http.get("/kpi-catalog", { params }).then((r) => r.data),
  create: (body) => http.post("/admin/kpi-catalog", body).then((r) => r.data),
  update: (id, body) => http.put(`/admin/kpi-catalog/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/admin/kpi-catalog/${id}`).then((r) => r.data),
  deleteImpact: (id) => http.get(`/admin/kpi-catalog/${id}/delete-impact`).then((r) => r.data),
  resetDefaults: () => http.post("/admin/kpi-catalog/reset-defaults").then((r) => r.data),
};

export const kpiMatrix = {
  get: (sid, period) => http.get(`/suppliers/${sid}/kpi-matrix-direct`, { params: period ? { period } : {} }).then((r) => r.data),
  upsert: (sid, body) => http.put(`/admin/suppliers/${sid}/kpi-capture-direct`, body).then((r) => r.data),
  batch: (items) => http.put("/admin/kpi-capture-direct/batch", { items }).then((r) => r.data),
};

// Spec 004: period the monthly capture screen shows and the latest one the user may capture.
export const capturaPeriodStatus = () => http.get("/captura-mensual/period-status").then((r) => r.data);

export const supplierTargets = {
  get: (sid) => http.get(`/suppliers/${sid}/targets`).then((r) => r.data),
  set: (sid, body) => http.put(`/admin/suppliers/${sid}/targets`, body).then((r) => r.data),
};

export const learning = {
  list: (params = {}) => http.get("/learning", { params }).then((r) => r.data),
  create: (body) => http.post("/learning", body).then((r) => r.data),
  resources: {
    list: (params = {}) => http.get("/learning/resources", { params }).then((r) => r.data),
    listAdmin: (params = {}) => http.get("/admin/learning/resources", { params }).then((r) => r.data),
    create: (body) => http.post("/admin/learning/resources", body).then((r) => r.data),
    update: (id, body) => http.put(`/admin/learning/resources/${id}`, body).then((r) => r.data),
    remove: (id) => http.delete(`/admin/learning/resources/${id}`).then((r) => r.data),
  },
  courses: {
    list: (params = {}) => http.get("/learning/courses", { params }).then((r) => r.data),
    get: (id) => http.get(`/learning/courses/${id}`).then((r) => r.data),
    create: (body) => http.post("/admin/learning/courses", body).then((r) => r.data),
    update: (id, body) => http.put(`/admin/learning/courses/${id}`, body).then((r) => r.data),
    remove: (id) => http.delete(`/admin/learning/courses/${id}`).then((r) => r.data),
  },
  progress: {
    list: () => http.get("/learning/progress").then((r) => r.data),
    upsert: (body) => http.post("/learning/progress", body).then((r) => r.data),
  },
};

export const reverse360 = {
  dimensions: () => http.get("/reverse-questionnaire/dimensions").then((r) => r.data),
  questions: () => http.get("/reverse-questions").then((r) => r.data),
  mine: (period) => http.get("/reverse-questionnaire/mine", { params: { period } }).then((r) => r.data),
  submit: (body) => http.post("/reverse-questionnaire", body).then((r) => r.data),
  summary: (params = {}) => http.get("/admin/reverse-questionnaire/summary", { params }).then((r) => r.data),
  admin: {
    listQuestions: () => http.get("/reverse-questions").then((r) => r.data),
    createQuestion: (body) => http.post("/admin/reverse-questions", body).then((r) => r.data),
    updateQuestion: (id, body) => http.put(`/admin/reverse-questions/${id}`, body).then((r) => r.data),
    deleteQuestion: (id) => http.delete(`/admin/reverse-questions/${id}`).then((r) => r.data),
  },
};

export const sustainability = {
  get: (sid) => http.get(`/suppliers/${sid}/sustainability-badge`).then((r) => r.data),
  set: (sid, body) => http.put(`/admin/suppliers/${sid}/sustainability-badge`, body).then((r) => r.data),
};


export default http;
